import LoginImage from "@/assets/login.jpg";
import useAuth from "@/shared/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/shared/hooks/use-toast";
import useFormValidation from "@/shared/form-validation/use-form-validation";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { useGoogleOAuth } from "@/shared/hooks/use-google-oauth";
import { authControllerIsAuthenticated } from "@/shared/client";
import { HttpStatusCode } from "axios";

type LoginForm = {
  email: string;
  password: string;
};

export default function LoginPage() {
  const navigate = useNavigate();
  const t = useTranslations();
  const { login, getAuthRedirectPath } = useAuth();
  const { handleGoogleOAuth, isSubmitting: isOAuthSubmitting } =
    useGoogleOAuth();

  const [form, setForm] = useState<LoginForm>({
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errors, validateField, safeParse } = useFormValidation<LoginForm>(
    form,
    z.object({
      email: z.email(t("login.validation.invalidEmail")),
      password: z.string().min(8, t("login.validation.passwordMinLength")),
    })
  );
  const { toast } = useToast();

  // Check authentication status on page load
  // Only run this check if we're not currently submitting (to avoid interfering with login redirect)
  useEffect(() => {
    if (isSubmitting) return; // Don't check auth while login is in progress

    const checkInitialAuthStatus = async () => {
      try {
        const response = await authControllerIsAuthenticated();
        if (response.status === HttpStatusCode.Ok && response.data?.user) {
          // User is already authenticated, use centralized helper to determine redirect
          const redirectPath = getAuthRedirectPath(response.data.user);
          navigate(redirectPath, { replace: true });
        }
      } catch {
        // User is not authenticated, stay on login page
      }
    };

    checkInitialAuthStatus();
  }, [navigate, isSubmitting, getAuthRedirectPath]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
    validateField(id as keyof LoginForm, value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const result = safeParse(form);
    if (result.success) {
      try {
        setIsSubmitting(true);
        const user = await login(form.email, form.password);
        if (user) {
          // Use centralized helper to determine redirect path based on fresh user object
          const redirectPath = getAuthRedirectPath(user);
          navigate(redirectPath, { replace: true });
        } else {
          toast({
            title: t("login.toast.loginFailed.title"),
            description: t("login.toast.loginFailed.description"),
            variant: "destructive",
          });
        }
      } catch {
        toast({
          title: t("login.toast.error.title"),
          description: t("login.toast.error.description"),
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="w-full lg:grid lg:min-h-screen lg:grid-cols-2 xl:min-h-screen">
      <Button
        variant="link"
        onClick={() => navigate("/")}
        className="fixed left-4 top-4 text-sm text-muted-foreground hover:text-foreground z-10"
      >
        ← {t("back")}
      </Button>
      <div className="flex items-center justify-center py-12">
        <div className="mx-auto grid w-[350px] gap-6">
          <div className="grid gap-2 text-center">
            <h1 className="text-3xl font-bold">{t("login.title")}</h1>
            <p className="text-balance text-muted-foreground">
              {t("login.subtitle")}
            </p>
          </div>
          <div className="grid gap-4">
            {/* Google Login Button */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleOAuth}
              disabled={isOAuthSubmitting}
              className="w-full h-11 text-base font-medium"
              tabIndex={1}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t("login.continueWithGoogle")}
            </Button>

            {/* OR Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t("login.or")}
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("login.emailPlaceholder")}
                  className="text-base"
                  required
                  value={form.email}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  tabIndex={2}
                  autoComplete="email"
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-red-500">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("login.password")}</Label>
                  <Link
                    to="/reset-password"
                    className="ml-auto inline-block text-sm underline"
                  >
                    {t("login.forgotPassword")}
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  className="text-base"
                  required
                  value={form.password}
                  onChange={handleInputChange}
                  aria-invalid={!!errors.password}
                  aria-describedby={
                    errors.password ? "password-error" : undefined
                  }
                  tabIndex={3}
                  autoComplete="current-password"
                />
                {errors.password && (
                  <p id="password-error" className="text-sm text-red-500">
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="grid gap-6">
                <Button
                  type="submit"
                  className="w-full bg-green-400 hover:bg-green-500 text-black hover:text-white"
                  disabled={isSubmitting}
                  tabIndex={4}
                >
                  {isSubmitting ? t("login.loggingIn") : t("login.login")}
                </Button>
                <div className="text-center text-sm">
                  {t("login.dontHaveAccount")}{" "}
                  <Link
                    to="/signup"
                    className="font-medium text-primary underline underline-offset-4"
                  >
                    {t("login.signup")}
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="hidden bg-muted lg:block h-screen">
        <img
          src={LoginImage}
          alt={t("login.imageAlt")}
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
}
